/**
 * Escape de conteúdo interpolado no HTML.
 *
 * Este módulo é a fronteira de segurança do renderizador. Nome de cliente, nome de projeto, cargo,
 * texto de seção — tudo vem de entrada humana e termina dentro de um documento servido em domínio da
 * eMaster. Sem escape, um nome de cliente contendo `<script>` viraria execução de código na máquina
 * de quem abre a proposta.
 *
 * Regra do pacote: **nenhum valor dinâmico entra no HTML sem passar por uma das funções daqui.**
 */

const MAPA_HTML: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escapa texto para uso em conteúdo de elemento ou em valor de atributo entre aspas.
 *
 * `&` é substituído primeiro pela própria natureza da varredura em uma passada, o que evita a
 * dupla codificação que aconteceria ao aplicar substituições em sequência.
 */
export function escaparHtml(valor: string): string {
  return valor.replace(/[&<>"']/g, (c) => MAPA_HTML[c] ?? c);
}

/**
 * Escapa e envolve em aspas duplas, para atributo.
 *
 * Usar esta função em vez de montar `class="${...}"` à mão elimina a classe de erro em que o valor
 * contém uma aspa e escapa do atributo.
 */
export function atributo(valor: string): string {
  return `"${escaparHtml(valor)}"`;
}

/**
 * Codifica um valor para uso em componente de query string ou em `mailto:`.
 *
 * `encodeURIComponent` não escapa `!'()*`, que são válidos em URL mas problemáticos em alguns
 * clientes de e-mail. O tratamento extra evita assunto truncado quando o nome do cliente tem
 * parêntese.
 */
export function codificarUrl(valor: string): string {
  return encodeURIComponent(valor).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

/**
 * Escapa texto para dentro de um literal de string JavaScript.
 *
 * Necessário porque o documento embute um script pequeno para a sinalização de vencimento. Além dos
 * caracteres usuais, `<` é escapado para impedir que a sequência `</script>` dentro de um dado
 * feche o bloco de script prematuramente — vetor clássico de injeção.
 */
export function escaparJs(valor: string): string {
  return valor
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
    .replace(/</g, '\\x3C');
}

/**
 * Sanitiza o HTML vindo do editor rich-text.
 *
 * Abordagem de **lista de permissão**: apenas formatação de texto sobrevive. Tudo o mais é
 * removido, inclusive `<script>`, `<style>`, `<iframe>`, atributos de evento e URLs `javascript:`.
 *
 * Escolha deliberada de não usar biblioteca: o núcleo é livre de dependências e não tem DOM
 * disponível. Em contrapartida, a lista é restritiva de propósito — na dúvida, remove.
 */
const TAGS_PERMITIDAS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'blockquote',
  'code',
  'pre',
  'a',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
]);

export function sanitizarHtmlRico(bruto: string): string {
  let saida = bruto;

  // Remove blocos inteiros cujo conteúdo é executável ou estilizável.
  saida = saida.replace(/<(script|style|iframe|object|embed|form|svg)\b[\s\S]*?<\/\1\s*>/gi, '');
  // Remove as mesmas tags em forma auto-fechada ou sem par.
  saida = saida.replace(/<\/?(script|style|iframe|object|embed|form|svg)\b[^>]*>/gi, '');
  // Remove comentários, que podem esconder markup condicional.
  saida = saida.replace(/<!--[\s\S]*?-->/g, '');

  // Reescreve cada tag restante, mantendo só o que está na lista de permissão.
  saida = saida.replace(
    /<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g,
    (tagCompleta, nome: string, atributos: string) => {
      const tag = nome.toLowerCase();
      if (!TAGS_PERMITIDAS.has(tag)) return '';

      const fechamento = tagCompleta.startsWith('</');
      if (fechamento) return `</${tag}>`;

      // O único atributo aceito é o href de âncora, e apenas com esquema seguro.
      if (tag === 'a') {
        const href = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(atributos);
        const valor = (href?.[1] ?? href?.[2] ?? href?.[3] ?? '').trim();
        const seguro = /^(https:\/\/|mailto:|#)/i.test(valor);
        return seguro
          ? `<a href=${atributo(valor)} target="_blank" rel="noopener noreferrer">`
          : '<a>';
      }

      return `<${tag}>`;
    },
  );

  return saida.trim();
}
